import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getLeads, updateLead, setEstatus, normalizeHour, normalizeDate,
  getStock, addStockItem, updateStockItem, deleteStockItem, uploadStockImage,
} from '../api';

const DataContext = createContext(null);

function load(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch (e) { return fallback; }
}

// Mapeo de columnas del Sheet de ACERO & CRISTAL MTY
function leadToCliente(lead) {
  return {
    id: 'sheet_' + lead._rowIndex,
    rowIndex: lead._rowIndex,
    source: 'sheet',
    fecha: normalizeDate(lead['Fecha de solicitud']),
    hora: normalizeHour(lead['Fecha de solicitud']),
    nombre: String(lead['Nombre completo'] || '').trim(),
    whatsapp: String(lead['WhatsApp'] || '').trim(),
    servicio: String(lead['Tipo de servicio'] || '').trim(),
    equipo: String(lead['Equipo relacionado'] || '').trim(),
    descripcion: String(lead['Descripción del interés o problema'] || '').trim(),
    urgencia: String(lead['Urgencia'] || '').trim(),
    ciudad: String(lead['Ciudad'] || '').trim(),
    atiende: String(lead['Atendió en Mostrador'] || '').trim(),
    tecnico: String(lead['Técnico que atendió'] || '').trim(),
    notaSheet: String(lead['Nota'] || '').trim(),
    estatus: String(lead['Estatus'] || '').trim()
  };
}

export const ESTADOS_CITA = [
  'Reservada', 'Confirmada', 'En servicio', 'Completada',
  'Cancelada x cliente', 'Cancelada x empresa', 'No asistió'
];

export function DataProvider({ children }) {
  const [clientes, setClientes] = useState(() => load('dc_clientes', []));
  const [citas, setCitas] = useState(() => load('dc_citas', []));
  const [notas, setNotas] = useState(() => load('dc_notas', []));
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  // Inventario: vive en el MISMO Sheet que lee el agente de WhatsApp,
  // por eso no se cachea en localStorage — siempre se pide fresco.
  const [stock, setStock] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  useEffect(() => { localStorage.setItem('dc_clientes', JSON.stringify(clientes)); }, [clientes]);
  useEffect(() => { localStorage.setItem('dc_citas', JSON.stringify(citas)); }, [citas]);
  useEffect(() => { localStorage.setItem('dc_notas', JSON.stringify(notas)); }, [notas]);

  // ── Importar leads del Sheet ──
  const importLeads = useCallback(async () => {
    setLoading(true);
    try {
      const leads = await getLeads();
      const nuevos = leads.map(leadToCliente).filter(c => c.nombre);
      setClientes(nuevos);
      // Citas auto-generadas desde el Sheet (no duplicar las manuales)
      setCitas(prev => {
        const manuales = prev.filter(c => !c.auto);
        const autos = nuevos
          .filter(c => c.fecha)
          .map(c => {
            const prevAuto = prev.find(x => x.auto && x.clienteId === c.id);
            return {
              id: 'cita_' + c.id,
              auto: true,
              clienteId: c.id,
              nombre: c.nombre,
              fecha: c.fecha,
              hora: prevAuto ? prevAuto.hora : (c.hora || ''),
              servicio: c.servicio,
              estado: prevAuto ? prevAuto.estado : 'Reservada',
              atiende: c.atiende,
              tecnico: c.tecnico,
              notas: prevAuto ? prevAuto.notas : ''
            };
          });
        return [...manuales, ...autos];
      });
      setLastSync(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { importLeads(); }, [importLeads]);

  // ── Toggle "Agendado" → escribe Estatus en el Sheet ──
  const toggleAgendado = async (cliente) => {
    const nuevo = cliente.estatus === 'Agregado' ? '' : 'Agregado';
    setClientes(prev => prev.map(c => (c.id === cliente.id ? { ...c, estatus: nuevo } : c)));
    await setEstatus(cliente.rowIndex, nuevo);
  };

  // ── Actualizar campos de gestión (mostrador / técnico / nota) ──
  const updateCliente = async (cliente, fields) => {
    setClientes(prev => prev.map(c => (c.id === cliente.id ? { ...c, ...fields } : c)));
    const payload = { rowIndex: cliente.rowIndex };
    if (fields.atiende !== undefined) payload.atiende = fields.atiende;
    if (fields.tecnico !== undefined) payload.asignado = fields.tecnico;
    if (fields.nuevaNota) payload.nota = fields.nuevaNota;
    await updateLead(payload);
  };

  // ── Inventario / catálogo ──
  const cargarStock = useCallback(async () => {
    setStockLoading(true);
    try { setStock(await getStock()); }
    finally { setStockLoading(false); }
  }, []);

  const crearStockItem = async (item) => {
    await addStockItem(item);
    await cargarStock();
  };

  const editarStockItem = async (rowIndex, item) => {
    // Optimista: la escritura va con fetch no-cors y no devuelve confirmacion.
    setStock(prev => prev.map(p => (p.rowIndex === rowIndex ? { ...p, ...item } : p)));
    await updateStockItem(rowIndex, item);
    await cargarStock();
  };

  const eliminarStockItem = async (rowIndex) => {
    setStock(prev => prev.filter(p => p.rowIndex !== rowIndex));
    await deleteStockItem(rowIndex);
    await cargarStock();
  };

  const subirFotoStock = async (base64, filename, mimeType) => {
    const r = await uploadStockImage(base64, filename, mimeType);
    return (r && r.foto_url) || '';
  };

  // ── Citas manuales ──
  const addCita = (cita) => {
    const nueva = { ...cita, id: 'cita_m_' + Date.now(), auto: false };
    setCitas(prev => [...prev, nueva]);
    return nueva;
  };
  const updateCita = (id, data) => setCitas(prev => prev.map(c => (c.id === id ? { ...c, ...data } : c)));
  const removeCita = (id) => setCitas(prev => prev.filter(c => c.id !== id));

  // ── Notas de servicio ──
  const addNota = async (clienteId, texto, autor) => {
    const nota = {
      id: 'nota_' + Date.now(),
      clienteId, texto, autor,
      fecha: new Date().toISOString()
    };
    setNotas(prev => [...prev, nota]);
    const cliente = clientes.find(c => c.id === clienteId);
    if (cliente && cliente.rowIndex) {
      const firmada = `${texto} — ${autor} (${new Date().toLocaleDateString('es-MX')})`;
      await updateLead({ rowIndex: cliente.rowIndex, nota: firmada });
      setClientes(prev => prev.map(c =>
        c.id === clienteId
          ? { ...c, notaSheet: c.notaSheet ? c.notaSheet + '\n──\n' + firmada : firmada }
          : c
      ));
    }
    return nota;
  };

  return (
    <DataContext.Provider value={{
      clientes, citas, notas, loading, lastSync,
      importLeads, toggleAgendado, updateCliente,
      addCita, updateCita, removeCita, addNota,
      stock, stockLoading, cargarStock,
      crearStockItem, editarStockItem, eliminarStockItem, subirFotoStock
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() { return useContext(DataContext); }
